import { createStore, SetState } from '@oneappexchange/lwc-wired';
import { State } from 'zustand';
import { FAVORITES_LIMIT, IS_DEVELOPMENT, REVIEW_ELIGIBILITY_PERIOD_DAYS, TBID_MOCK_USER } from '~/modules/config';
import { AuthTimeoutError } from '~/modules/errors/authErrors';
import { FavoriteListingsService } from '~/modules/services/favoriteListingsService';
import { SessionIdService } from '~/modules/services/sessionIdService';
import { featureFlagsStore } from '~/modules/stores/feature_flags_store';
import { i18nStore } from '~/modules/stores/i18nStore';
import { parseJson } from '~/modules/utilities/object_utils';
import { getLoginUrl } from '~/modules/utilities/router';
import { StoreActionResponse } from '~/modules/utilities/storesUtilities';
import { getDaysSinceSignup, isValidSignupDate } from '~/modules/utilities/time';
import { UserContext, UserInfo, UserService } from '../services/userService';
import { UserTokenService } from '../services/userTokenService';
import { logger } from '~/modules/utilities/logger';
import { AuthenticationSuccessLog } from '~/types/auth-log';

const MOCK_USER_LOCAL_STORAGE_KEY = 'MOCK_USER';

const userService = new UserService();
const favoritesService = new FavoriteListingsService();

type NotificationActionKey = keyof I18nMessages['notifications']['userStore'];

function getNotificationLabels(key: NotificationActionKey) {
    const authMessages = i18nStore.getState().i18n.notifications.authentication;
    const notificationLabels = key ? i18nStore.getState().i18n.notifications.userStore[key] : undefined;
    return { authMessages, notificationLabels };
}

export type TBIDAuthFlow = 'login' | 'signup';

export type UserProfile = {
    isGuestUser: boolean;
    avatar: string;
    profileUrl: string;
};

type AuthenticatedUserInfo = UserInfo & {
    isGuestUser: false;
};

export enum SavedListingsLoadingStatus {
    UNINITILIAZED = 'UNINITILIAZED',
    LOADED = 'LOADED',
    LOADING = 'LOADING',
    SAVING = 'SAVING',
}

export enum RemoveFavoriteActionStatus {
    UNINITILIAZED = 'UNINITILIAZED',
    SUCCESS = 'SUCCESS',
    LOADING = 'LOADING',
    ERROR = 'ERROR',
}

export interface LastRemovedFavoriteState {
    status: RemoveFavoriteActionStatus;
    listingId?: string;
    error?: Error;
}

type TpalPropertyNames = `ThirdPartyAccountLink${number}`;
interface TbidCustomAttributes {
    LastLoggedInFrom: string;
    ThirdPartyAccountLinksNumber: number;
    [key: TpalPropertyNames]: string;
    user_id: string;
    CompanyName: string;
    RelationshipToSalesforce: string;
    Role: string;
    SignupService: string;
    SignupDate: string;
}

export interface TbidProfile {
    sub: string;
    user_id: string;
    family_name: string;
    given_name: string;
    email: string;
    email_verified: boolean;
    zoneinfo: string;
    photos: {
        picture: string;
        thumbnail: string;
    };
    picture: string;
    profile: string;
    address: {
        region: string;
        country: string;
    };
    active: boolean;
    updated_at: string;
    custom_attributes: TbidCustomAttributes;
    newRegistration: boolean;
    isAvailableForHire: boolean;
    preferred_username: string;
    organization_id: string;
    nickname: string;
    name: string;
    user_type: string;
    language: string;
    locale: string;
    utcOffset: number;
    is_app_installed: boolean;
    urls: {
        enterprise: string;
        metadata: string;
        partner: string;
        rest: string;
        sobjects: string;
        search: string;
        query: string;
        recent: string;
        tooling_soap: string;
        tooling_rest: string;
        profile: string;
        feeds: string;
        groups: string;
        users: string;
        feed_items: string;
        feed_elements: string;
        custom_domain: string;
    };
    access_token: string;
}

export interface TpalInfo {
    userId: string;
    orgId: string;
    userName: string;
    orgEdition: string;
    orgStatus: string;
}

export interface UserPerm {
    userId: string;
    orgId: string;
    canCheckout: boolean;
}

export interface UserState extends State {
    isLoading: boolean;
    isLoadingTbid: boolean;
    // come from oidc interchange
    tbidProfile?: TbidProfile;
    access_token?: string;
    id_token?: string;
    auth_flow?: TBIDAuthFlow;
    auth_origin?: string;
    tbidCompanyName?: string;
    tbidRole?: string;
    tbidUserId?: string;
    tbidOrganizationId?: string;
    tbidRelationshipToSalesforce?: string;
    lastLoggedInFromTpal?: TpalInfo;
    tpalInfo?: TpalInfo[];
    userPerms: UserPerm[];

    // rest of user info comes from REST calls to endpoint(s)
    name?: string;
    profileUrl?: string;
    backgroundImageUrl?: string;
    userPicture?: string;

    //these values only come from the User Context Service (which calls the AppExchange Org User Service)
    userId?: string;
    canInstall?: boolean;
    canCheckout?: boolean;
    savedListings?: string[];
    userContext?: UserContext;
    recentActivity?: string[];

    // synthetic values
    isGuestUser: boolean;
    userToken: string;
    tokenExpirationDate: number;

    // favorites
    savedListingsLoadingStatus: SavedListingsLoadingStatus;
    lastRemovedFavorite: LastRemovedFavoriteState;

    //try-buy form
    firstName?: string;
    lastName?: string;
    company?: string;
    country?: string;
    state?: string;
    email?: string;
    phone?: string;
}

export const initialState: UserState = {
    isLoading: true,
    isLoadingTbid: true,
    // @ts-expect-error * Automated Type Error Fix
    access_token: null,
    // @ts-expect-error * Automated Type Error Fix
    id_token: null,
    // @ts-expect-error * Automated Type Error Fix
    profileUrl: null,
    // @ts-expect-error * Automated Type Error Fix
    backgroundImageUrl: null,
    // @ts-expect-error * Automated Type Error Fix
    name: null,
    canInstall: false,
    canCheckout: false,
    // @ts-expect-error * Automated Type Error Fix
    savedListings: null,
    // @ts-expect-error * Automated Type Error Fix
    userPicture: null,
    isGuestUser: true,
    // @ts-expect-error * Automated Type Error Fix
    userToken: null,
    // @ts-expect-error * Automated Type Error Fix
    tokenExpirationDate: null,
    userContext: {
        // @ts-expect-error * Automated Type Error Fix
        orgid: null,
        // @ts-expect-error * Automated Type Error Fix
        orgstatus: null,
        // @ts-expect-error * Automated Type Error Fix
        orgedition: null,
        topCategories: [],
        // @ts-expect-error * Automated Type Error Fix
        mainCategory: null,
        products: [],
        // @ts-expect-error * Automated Type Error Fix
        region: null,
        // @ts-expect-error * Automated Type Error Fix
        industry: null,
        // @ts-expect-error * Automated Type Error Fix
        userRole: null,
        userLinkedOrgs: [],
        lastInstalledListings: [],
    },
    savedListingsLoadingStatus: SavedListingsLoadingStatus.UNINITILIAZED,
    lastRemovedFavorite: { status: RemoveFavoriteActionStatus.UNINITILIAZED },
};

export const selectUserId = (userState: UserState) => {
    return userState.userId;
};

export const selectIsGuestUser = (userState: UserState) => {
    return userState.isGuestUser;
};

export const selectUserContext = (userState: UserState) => {
    return userState.userContext;
};

export const selectUserReviewEligibility = (userState: UserState) => {
    const signupDate = userState.tbidProfile?.custom_attributes?.SignupDate;
    const daysSinceSignup = getDaysSinceSignup(signupDate);
    // A present-but-unparseable date should fail open (allow), same as a
    // missing date. A valid date (including today = 0 days) must be gated.
    const isInvalidDate = !!signupDate && !isValidSignupDate(signupDate);
    return {
        isLoading: userState.isLoading,
        isGuestUser: userState.isGuestUser,
        canWriteReview: !signupDate || isInvalidDate || daysSinceSignup >= REVIEW_ELIGIBILITY_PERIOD_DAYS,
    };
};

export const selectUserToken = (userState: UserState) => {
    return userState.userToken;
};

export const selectAuthenticationFlow = (userState: UserState) => {
    return { auth_flow: userState.auth_flow, auth_origin: userState.auth_origin, isGuestUser: userState.isGuestUser };
};

export const selectProfile = (userState: UserState) => {
    return { isGuestUser: userState.isGuestUser, avatar: userState.userPicture, profileUrl: userState.profileUrl };
};

export const selectStatus = (userState: UserState) => {
    return { isLoading: userState.isLoading, isGuestUser: userState.isGuestUser };
};

export function selectSavedListingsCount(userState: UserState): string {
    if (!Array.isArray(userState.savedListings)) {
        return '';
    }
    const numSavedListings = userState.savedListings.length;
    return String(numSavedListings > FAVORITES_LIMIT ? FAVORITES_LIMIT : numSavedListings);
}

export function selectSavedListings(userState: UserState): string[] {
    return userState.savedListings?.slice(0, FAVORITES_LIMIT) || [];
}

export function selectPropertiesForFavoriteStore(userState: UserState) {
    return {
        savedListings: userState.savedListings,
        isGuestUser: userState.isGuestUser,
        isLoading: userState.isLoading,
        savedListingsLoadingStatus: userState.savedListingsLoadingStatus,
        lastRemovedFavorite: userState.lastRemovedFavorite,
    };
}

export const selectUserFavorite = (userState: UserState) => {
    return {
        userToken: userState.userToken,
        userId: userState.userId,
        savedListings: userState.savedListings,
        isGuestUser: userState.isGuestUser,
        isLoading: userState.isLoading,
        savedListingsLoadingStatus: userState.savedListingsLoadingStatus,
    };
};

export const selectTPALOrgIds = (userState: UserState): TpalInfo[] | undefined => {
    return userState.tpalInfo;
};

export const selectOrgIdOptions = (userState: UserState): { value: string; label: string }[] | undefined => {
    const TPALOrgIds = selectTPALOrgIds(userState);
    return Array.isArray(TPALOrgIds)
        ? TPALOrgIds.map(({ orgId, userName }: TpalInfo) => ({
              value: orgId,
              label: userName,
          }))
        : [];
};

/**
 * Selects user fields needed for lead collection forms
 * Used by offer request and trial forms to pre-populate user data
 */
export function selectLeadFormFields(userState: UserState) {
    return {
        isGuestUser: userState.isGuestUser,
        isLoading: userState.isLoading,
        firstName: userState.firstName,
        lastName: userState.lastName,
        company: userState.company,
        country: userState.country,
        state: userState.state,
        email: userState.email,
    };
}

export const parseTpal = (tpal?: string): TpalInfo | undefined => {
    if (tpal != null) {
        const tpalMatch =
            /salesforce:+(00D[a-zA-Z0-9]{15})+(005[a-zA-Z0-9]{15}):(.+?):([a-zA-Z0-9\s]+):([a-zA-Z0-9\s]+)/.exec(tpal);
        if (tpalMatch) {
            return {
                orgId: tpalMatch[1],
                userId: tpalMatch[2],
                userName: tpalMatch[3],
                orgEdition: tpalMatch[4],
                orgStatus: tpalMatch[5],
            };
        }
    }
    return undefined;
};

const getTPALInfo = (tbidProfile: TbidProfile): TpalInfo[] => {
    const tpalLinks: TpalInfo[] = [];
    const tpalinksCount = tbidProfile?.custom_attributes?.ThirdPartyAccountLinksNumber || 0;
    /* Sample TPAL format for reference. 
      ThirdPartyAccountLink1=google:112806924891038888282:bearcodey@gmail.com, 
      ThirdPartyAccountLink2=salesforce:00DW0000008xQQ9MAM005W0000003Yn9NIAS:bearcodey@dev1.cs192:Enterprise Edition:DEMO:Standard:true, 
      ThirdPartyAccountLinksNumber=2,
    */
    for (let i = 1; i <= tpalinksCount; i++) {
        const tpalink = `ThirdPartyAccountLink${i}` as TpalPropertyNames;
        const tpalinkValue = tbidProfile?.custom_attributes[tpalink];
        const parsedTpal = parseTpal(tpalinkValue);
        if (parsedTpal) {
            tpalLinks.push(parsedTpal);
        }
    }
    return tpalLinks;
};

function applyTbidProfile(draft: UserState, tbidProfile: TbidProfile): void {
    draft.tbidProfile = tbidProfile;
    draft.tpalInfo = getTPALInfo(tbidProfile);
    draft.lastLoggedInFromTpal = parseTpal(tbidProfile.custom_attributes?.LastLoggedInFrom);
    draft.isLoadingTbid = false;
}

function applyAuthenticationFlow(draft: UserState, flow: string, origin?: string): void {
    draft.auth_flow = flow === 'signup' ? 'signup' : 'login';
    draft.auth_origin = origin || 'header';
}

function applyOrgUserInfo(draft: UserState, orgUserInfo: UserInfo): void {
    draft.isGuestUser = orgUserInfo.isGuestUser;
    if (draft.isGuestUser) {
        draft.isLoading = false;
        return;
    }

    UserTokenService.setUserToken(orgUserInfo.userToken, orgUserInfo.tokenExpirationDate);
    draft.name = i18nStore.getState().i18n.shared.userName({
        FIRST_NAME: orgUserInfo.firstName ?? '',
        LAST_NAME: orgUserInfo.lastName ?? '',
    });
    draft.userId = orgUserInfo.userId;
    draft.profileUrl = orgUserInfo.tbidProfileUrl;
    draft.userPicture = orgUserInfo.userPicture;
    draft.savedListings = orgUserInfo.savedListings;
    draft.canInstall = orgUserInfo.canInstall;
    draft.canCheckout = orgUserInfo.canCheckout;
    draft.userContext = orgUserInfo.userContext;
    draft.userToken = orgUserInfo.userToken;
    draft.recentActivity = orgUserInfo.recentActivity;
    draft.tokenExpirationDate = orgUserInfo.tokenExpirationDate;
    draft.firstName = orgUserInfo.firstName;
    draft.lastName = orgUserInfo.lastName;
    draft.country = orgUserInfo.country;
    draft.state = orgUserInfo.state;
    draft.email = orgUserInfo.email;
    draft.phone = orgUserInfo.phone;
    draft.company = orgUserInfo.company;
    draft.savedListingsLoadingStatus = SavedListingsLoadingStatus.LOADED;
    draft.isLoading = false;

    const authLog: AuthenticationSuccessLog = {
        message: 'Authentication attempt',
        status: 'success',
        authMethod: 'client-auth',
        timestamp: new Date().toISOString(),
        userId: orgUserInfo.userId,
    };
    logger.info(authLog);
}

export const userStore = createStore(
    initialState,

    (set: SetState<UserState>, get: GetState<UserState>) => ({
        setLoading(isLoading: boolean) {
            set(function setLoading(draft: UserState) {
                draft.isLoading = isLoading;
            });
        },
        setAuthenticationFlow(flow: string, origin?: string) {
            set(function updateAuthenticationFlow(draft: UserState) {
                applyAuthenticationFlow(draft, flow, origin);
            });
        },
        setTbidProfile(tbidProfile: TbidProfile) {
            set(function setTbidProfile(draft) {
                applyTbidProfile(draft, tbidProfile);
            });
        },
        setTPalInfo(tpalInfo: TpalInfo[]) {
            set(function setTpalInfo(draft) {
                draft.tpalInfo = tpalInfo;
            });
        },
        setUserPerms(perms: UserPerm[]) {
            set(function setUserPerms(draft: UserState) {
                draft.userPerms = perms;
            });
        },
        // @ts-expect-error * Automated Type Error Fix
        setUser(user) {
            set(function updateUser(draft: UserState) {
                // this function will only called when using OIDC auth
                // just the fundamentals here: at this point we have a token and are sure that
                // user has authenticated.  access_token will be used to exchange for org SID tokens via gateway
                draft.isGuestUser = false;
                draft.access_token = user.access_token;
                draft.id_token = user.id_token;

                //save the background image url as it is not returned from the appexchange org call
                draft.backgroundImageUrl = user.profile?.custom_attributes?.backgroundImageUrl;
                draft.tbidCompanyName = user.profile?.custom_attributes?.CompanyName;
                draft.tbidUserId = user.profile?.custom_attributes?.user_id;
                draft.tbidRole = user.profile?.custom_attributes?.Role;
                draft.tbidRelationshipToSalesforce = user.profile?.custom_attributes?.RelationshipToSalesforce;
                draft.lastLoggedInFromTpal = parseTpal(user.profile?.custom_attributes?.LastLoggedInFrom);
                // @ts-expect-error * Automated Type Error Fix
                draft.tbidOrganizationId = userService.getOrganizationId(user);
            });
        },
        setOrgUserInfo(orgUserInfo: UserInfo) {
            set(function updateAppExchangeUser(draft: UserState) {
                if (orgUserInfo) {
                    applyOrgUserInfo(draft, orgUserInfo);
                } else {
                    draft.isLoading = false;
                }
            });
        },
        setIisAuthenticatedUser(
            tbidProfile: TbidProfile,
            orgUserInfo: AuthenticatedUserInfo,
            flow: string,
            origin?: string,
        ) {
            set(function setIisAuthenticatedUser(draft: UserState) {
                applyTbidProfile(draft, tbidProfile);
                applyOrgUserInfo(draft, orgUserInfo);
                applyAuthenticationFlow(draft, flow, origin);
            });
        },
        setGuestUser() {
            set(function setGuestUser() {
                return {
                    ...initialState,
                    isGuestUser: true,
                    isLoading: false,
                    isLoadingTbid: false,
                };
            });
        },
        fetchUserToken() {
            let userToken = selectUserToken(get());
            if (!userToken) {
                userToken = UserTokenService.getUserToken();
                set(function updateRecentItemResult(draft: UserState) {
                    draft.userToken = userToken;
                });
            }
            return userToken;
        },
        setMockedUser(mockedUser: UserState) {
            set(function setMockedUser() {
                // this function use the initial state and override it with the mock user
                // to prevent the store from being overwritten with an incomplete or wrong user format
                return {
                    ...initialState,
                    ...mockedUser,
                    /*
                        Do not change the isLoading value to prevent it from being true before the TBID widget
                        has finished loading and let the TBID component manage this property
                    */
                    isLoading: get().isLoading,
                };
            });
            localStorage.setItem(MOCK_USER_LOCAL_STORAGE_KEY, JSON.stringify(get()));
        },
        fetchAppExchangeUser() {
            return userService.getUser(true).then((orgUserInfo) => {
                userStore.setOrgUserInfo(orgUserInfo);
            });
        },
        async fetchUserPerms(tpalInfo: TpalInfo[]) {
            const perms = await userService.getTpalsUserPerms(tpalInfo);
            userStore.setUserPerms(perms);
        },
        async addFavorite(listingId: string) {
            const userId = get().userId;
            const loginUrl = getLoginUrl();
            const response: StoreActionResponse = { isSuccess: false };
            const { authMessages, notificationLabels } = getNotificationLabels('addFavorite');

            set(function beginAddingFavorite(draft: UserState) {
                draft.savedListingsLoadingStatus = SavedListingsLoadingStatus.LOADING;
            });

            try {
                // @ts-expect-error * Automated Type Error Fix
                await favoritesService.addToFavorites(userId, listingId);
                // @ts-expect-error * Automated Type Error Fix
                response.successMessage = notificationLabels.successMessage();
                response.isSuccess = true;

                set(function addedFavorite(draft: UserState) {
                    // @ts-expect-error * Automated Type Error Fix
                    draft.savedListings.unshift(listingId);
                    draft.savedListingsLoadingStatus = SavedListingsLoadingStatus.LOADED;
                });
            } catch (error) {
                response.exceptionInfo = {
                    context: 'userStore.addFavorite',
                    error,
                };

                if (error instanceof AuthTimeoutError) {
                    response.errorMessage = authMessages.errorMessage({ LOGIN_URL: loginUrl });
                } else {
                    // @ts-expect-error * Automated Type Error Fix
                    response.errorMessage = notificationLabels.errorMessage();
                }

                set(function errorAddingFavorite(draft: UserState) {
                    draft.savedListingsLoadingStatus = SavedListingsLoadingStatus.LOADED;
                });
            }

            return response;
        },
        async removeFavorite(listingId: string): Promise<StoreActionResponse> {
            const userId = get().userId;
            const loginUrl = getLoginUrl();
            const { authMessages, notificationLabels } = getNotificationLabels('removeFavorite');
            const response: StoreActionResponse = { isSuccess: false };

            set(function beginRemovingFavorite(draft: UserState) {
                draft.savedListingsLoadingStatus = SavedListingsLoadingStatus.LOADING;
                draft.lastRemovedFavorite.status = RemoveFavoriteActionStatus.LOADING;
            });

            try {
                // @ts-expect-error * Automated Type Error Fix
                await favoritesService.removeFavorite(userId, listingId);
                // @ts-expect-error * Automated Type Error Fix
                response.successMessage = notificationLabels.successMessage();
                response.isSuccess = true;

                set(function removedFavorite(draft: UserState) {
                    draft.savedListingsLoadingStatus = SavedListingsLoadingStatus.LOADED;
                    // @ts-expect-error * Automated Type Error Fix
                    draft.savedListings = draft.savedListings.filter((listing) => listing !== listingId);
                    draft.lastRemovedFavorite = {
                        status: RemoveFavoriteActionStatus.SUCCESS,
                        listingId,
                    };
                });
            } catch (error) {
                response.exceptionInfo = {
                    context: 'userStore.removeFavorite',
                    error,
                };

                if (error instanceof AuthTimeoutError) {
                    response.errorMessage = authMessages.errorMessage({ LOGIN_URL: loginUrl });
                } else {
                    // @ts-expect-error * Automated Type Error Fix
                    response.errorMessage = notificationLabels.errorMessage();
                }

                set(function errorRemovingFavorite(draft: UserState) {
                    draft.savedListingsLoadingStatus = SavedListingsLoadingStatus.LOADED;
                    draft.lastRemovedFavorite = {
                        status: RemoveFavoriteActionStatus.ERROR,
                        error,
                    };
                });
            }
            return response;
        },
        reset() {
            set(function resetStore() {
                return initialState;
            });
            if (IS_DEVELOPMENT && TBID_MOCK_USER) {
                localStorage.removeItem(MOCK_USER_LOCAL_STORAGE_KEY);
            }
        },
    }),
    'userStore',
    IS_DEVELOPMENT,
);


userStore.subscribe((userToken: string) => {
    if (!userToken) {
        return;
    }
    featureFlagsStore.getFeatureFlags({
        context: {
            userId: userToken,
            sessionId: SessionIdService.getSessionId(),
            hostname: window.location.hostname,
        },
        defaultValues: window.stores?.FEATURE_FLAGS,
    });
}, selectUserToken);

if (IS_DEVELOPMENT && TBID_MOCK_USER) {
    const mockedUserString = localStorage.getItem(MOCK_USER_LOCAL_STORAGE_KEY);
    if (mockedUserString) {
        const mockedUser = parseJson<UserState>(mockedUserString);
        userStore.setMockedUser(mockedUser);
    }
    userStore.subscribe((state) => localStorage.setItem(MOCK_USER_LOCAL_STORAGE_KEY, JSON.stringify(state)));
}

export function selectIsAuthenticated(state: UserState) {
    return state.tbidProfile?.active === true || state.isGuestUser === false;
}
